import clsx from 'clsx';

import DrupalImage from '@commons/components/Image/Drupal';
import RichText from '@commons/components/RichText';
import Text from '@commons/components/Text';
import Typewritter from '@commons/components/Typewritter';
import VideoPlayer from '@commons/components/VideoPlayer';
import Flex from '@commons/containers/Flex';
import { breakpoints } from '@commons/contexts/responsive';
import { asAbsoluteURL } from '@commons/helpers/url';
import { Link } from '@commons/i18n/navigation';
import type {
  DrupalBlock,
  DrupalImage as DrupalImageType,
  DrupalLink,
  DrupalRemoteVideo,
  DrupalVideo,
} from '@commons/types/drupal';

import styles from './index.module.scss';

// Type definition for the Tile component properties.

type Props = DrupalBlock<{
  title: string;
  body: string;
  media: DrupalImageType | DrupalVideo | DrupalRemoteVideo;
  tag?: string;
  link?: DrupalLink;
}>;

/**
 * Tile Block component.
 */

const FeatureBandBlock = ({ content }: Props) => {
  const { title, body, media, tag, link } = content;

  const renderedCard = (
    <Flex
      gap="sm"
      border="top"
      theme="white"
      paddingTop="md"
      paddingBottom="md"
      justify="space-between"
      direction={{ xs: 'column-reverse', lg: 'row' }}
      className={styles.wrapper}
    >
      <Flex gap="sm" direction="column" justify={'space-between'} width={{ xs: 12, lg: 3 }}>
        <RichText value={body} />
        {tag && (
          <Text as="span" color="grey-6" className={styles.tag}>
            {tag}
          </Text>
        )}
      </Flex>
      <Flex gap="sm" direction={{ xs: 'column', lg: 'row' }} width={{ xs: 12, lg: 8 }}>
        <Flex.Item width={{ xs: 12, lg: 6 }}>
          <FeatureBandMedia {...media} />
        </Flex.Item>
        <Flex.Item width={{ xs: 12, lg: 6 }} paddingLeft={{ xs: 'none', lg: 'md' }}>
          <Text as="h2" marginBottom="none" level="display-1" className={styles.title}>
            <Typewritter text={title.replaceAll(' ', '<br />')} />
          </Text>
        </Flex.Item>
      </Flex>
    </Flex>
  );

  if (link) {
    return <Link href={link.url}>{renderedCard}</Link>;
  }

  return renderedCard;
};

/**
 * FeatureBand Media component.
 *
 * Renders an image or video based on the media type.
 */

const FeatureBandMedia = (media: DrupalImageType | DrupalVideo | DrupalRemoteVideo) => {
  const { bundle, url } = media;

  const mediaURL = asAbsoluteURL(url);
  const isVideo = bundle === 'video';
  const isExternalVideo = bundle === 'remote_video';
  if (isVideo || isExternalVideo) {
    return (
      <VideoPlayer
        src={mediaURL}
        controls
        playsInline
        displayLoading={!isExternalVideo}
        className={clsx(styles.mediaWrapper, styles.video)}
      />
    );
  }

  return (
    <DrupalImage
      {...media}
      sizes={`(max-width: ${breakpoints.md}px) 100vw, (max-width: ${breakpoints.xl}px) 50vw, 800px`}
      className={clsx(styles.mediaWrapper, styles.image)}
    />
  );
};

export default FeatureBandBlock;
